import React, { useEffect, useState } from "react";
import { GetDeliveryBoysData, GetDeliveryBoysRatingData } from "../../lib/data"; // Ensure this imports your specific API function for ratings
import Pagination from "../../ui/paginations/Paginations";

// Helper for Star rendering
const StarRating = ({ rate, size = "md" }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`${
          size === "lg" ? "w-6 h-6" : "w-4 h-4"
        } ${i <= rate ? "text-yellow-400" : "text-gray-300"}`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex gap-1">{stars}</div>;
};

export default function DeliveryRatingsModal({ deliveryBoyId, isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rate: "0", ratings: [] });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [currentPage, setCurrentPage] = useState(1);

  // NOTE: You mentioned the function name is GetDeliveryBoysData. 
  // If you have a specific function for ratings (e.g. GetDeliveryBoyRatings), replace it here.
  // I am assuming a hypothetical "GetDeliveryBoyRatings" or you might be reusing a fetcher.
  // For now, I will use a placeholder name you should map to your actual API call.
  // const fetcher = GetDeliveryBoysRatings; 

  useEffect(() => {
    if (isOpen && deliveryBoyId) {
      fetchRatings();
    }
  }, [isOpen, deliveryBoyId, currentPage]);

  const fetchRatings = async () => {
    setLoading(true);
    try {
      // REPLACE with your actual API call logic
      // Example: const response = await GetDeliveryBoyRatings(deliveryBoyId, currentPage);
      
      // Simulating response based on your JSON for now so the UI works:
       const response = await GetDeliveryBoysRatingData(deliveryBoyId, currentPage); // <--- CHECK THIS FUNCTION NAME

      setData(response.data || { rate: "0", ratings: [] });
      setPagination(response.pagination || { current_page: 1, last_page: 1 });
    } catch (error) {
      console.error("Failed to fetch delivery ratings", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-4xl h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fade-in-up">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Delivery Reviews</h2>
            <p className="text-sm text-gray-500">Delivery Boy ID: #{deliveryBoyId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/30">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
              <p className="text-gray-400">Loading reviews...</p>
            </div>
          ) : (
            <>
              {/* Overall Rating Header */}
              <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6 flex items-center gap-6">
                <div className="text-center px-4 border-r border-gray-100">
                    <span className="block text-4xl font-extrabold text-gray-800">{data.rate}</span>
                    <span className="text-xs text-gray-400 uppercase tracking-wide">Average</span>
                </div>
                <div className="flex flex-col gap-1">
                    <StarRating rate={Math.round(Number(data.rate))} size="lg" />
                    <p className="text-sm text-gray-500">{pagination?.total || 0} Total Ratings</p>
                </div>
              </div>

              {/* Reviews List */}
              <div className="space-y-4">
                {data.ratings && data.ratings.length > 0 ? (
                  data.ratings.map((review) => (
                    <div key={review.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
                      
                      {/* Top Row: Meta & Financials */}
                      <div className="flex flex-col sm:flex-row justify-between sm:items-start gap-4 mb-4 border-b border-gray-50 pb-4">
                        <div className="flex items-center gap-3">
                           <div className="bg-purple-50 text-purple-600 rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm">
                              #{review.id}
                           </div>
                           <div>
                              <StarRating rate={review.rate} />
                              <span className="text-xs text-gray-400 mt-1 block">{review.date} at {review.time}</span>
                           </div>
                        </div>

                        {/* Financial Stats Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-right">
                           <div>
                             <span className="block text-[10px] uppercase text-gray-400 font-bold">Cash on User</span>
                             <span className="font-medium text-gray-800">{Number(review.cash_on_user).toLocaleString()}</span>
                           </div>
                           <div>
                             <span className="block text-[10px] uppercase text-gray-400 font-bold">Cash on Store</span>
                             <span className="font-medium text-gray-800">{Number(review.cash_on_store).toLocaleString()}</span>
                           </div>
                           <div>
                             <span className="block text-[10px] uppercase text-gray-400 font-bold">Profit</span>
                             <span className="font-medium text-green-600">+{Number(review.profit_delivery).toLocaleString()}</span>
                           </div>
                        </div>
                      </div>

                      {/* Store Info */}
                      {review.store && (
                        <div className="flex items-center gap-2 mb-3 text-sm text-gray-600 bg-gray-50 p-2 rounded-lg w-fit">
                          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span className="font-semibold">{review.store.name}</span>
                          <span className="text-gray-300">|</span>
                          <span>{review.store.address}</span>
                        </div>
                      )}

                      {/* Comment */}
                      {review.comment && (
                        <div className="text-sm text-gray-700 italic border-l-4 border-red-200 pl-3 mb-4">
                          "{review.comment}"
                        </div>
                      )}

                      {/* Items (Products) */}
                      {review.items && review.items.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[10px] font-bold text-gray-400 uppercase mb-2">Order Items</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {review.items.map((item) => (
                              <div key={item.id} className="flex items-center gap-3 p-2 rounded border border-gray-100 bg-gray-50/50">
                                <img 
                                  src={item.photo} 
                                  alt={item.title} 
                                  className="w-10 h-10 rounded object-cover bg-gray-200"
                                  onError={(e) => e.target.src = "https://via.placeholder.com/150?text=No+Img"} 
                                />
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-800 truncate">{item.title}</h4>
                                  <div className="flex justify-between text-xs mt-0.5">
                                    <span className="text-gray-500">x{item.quantity}</span>
                                    <span className="font-medium text-gray-900">{Number(item.price).toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                    <p className="text-gray-500">No ratings found for this delivery boy.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-white border-t border-gray-200">
           <Pagination
              currentPage={currentPage}
              totalPages={pagination?.last_page || 1}
              handleChangePage={(page) => setCurrentPage(page)}
            />
        </div>
      </div>
    </div>
  );
}